import React, {useMemo, useRef, useState} from 'react';
import i18n from '../../utils/i18n';
import Button from '@adsk/bim360-matrix-react-button';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import ModalConfirmation from '@adsk/bim360-matrix-react-modal-confirmation';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import Text from '@adsk/bim360-matrix-react-text';
import {NotificationTypes, NotificationLevels} from '@adsk/bim360-matrix-react-notification-manager';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TemplateCategoryRowContainer from './TemplateCategoryRowContainer';
import {templateCategoryType} from '../../constants/proptype-validation';
import TemplateCategoryNewRowContainer from './TemplateCategoryNewRowContainer';
import TemplateCategoryDeleteModalContainer from './TemplateCategoryDeleteModalContainer';
import {CATEGORY_DELETE_CONFIRM_METHOD} from '../../constants/issue-templates';
import useCategoryDelete from './useCategoryDelete';

const TemplateCategoriesManageModal = ({
  container,
  categories,
  isOpen,
  close,
  isCategoryCreateInProgress,
  fetchCategoryTemplatesAction,
  deleteTemplateCategoryAction,
  showNotificationAction
}) => {
  const [searchExpression, setSearchExpression] = useState('');
  const filteredCategories = useMemo(() => {
    const normalizedExpression = searchExpression.trim().toLowerCase();
    return categories.filter(category => category.title.toLowerCase().includes(normalizedExpression));
  }, [categories, searchExpression]);
  const [isNewCategoryRowVisible, setIsNewCategoryRowVisible] = useState(false);
  const listRef = useRef(null);
  const hideNewCategoryRow = () => {
    setIsNewCategoryRowVisible(false);
  };

  const {categoryToDelete, confirmMethod, onDeleteClicked, onDeleteConfirmed, onDeleteModalExited} = useCategoryDelete({
    fetchCategoryTemplatesAction,
    deleteTemplateCategoryAction,
    showNotificationAction
  });

  const onCreateCategorySuccess = () => {
    hideNewCategoryRow();
    showNotificationAction({
      type: NotificationTypes.TOAST,
      level: NotificationLevels.SUCCESS,
      message: i18n.t('templatesGrid.newModal.createdCategoryMessage')
    });
  };
  const onCreateCategoryError = () => {
    hideNewCategoryRow();
    showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: i18n.t('templatesGrid.newModal.createdCategoryErrorMessage')
    });
  };

  const shouldShowEmptyState = filteredCategories.length === 0 && !isNewCategoryRowVisible;

  return (
    <SimpleModal
      container={container}
      title={i18n.t('templatesGrid.manageCategoriesModal.title')}
      onExited={close}
      show={isOpen}
      modalBodyProps={{className: 'TemplateCategoriesManageModal__body'}}
      footer={<div className="TemplateCategoriesManageModal__footer">
        <Button styleType={Button.StyleType.PRIMARY} onClick={close}>
          {i18n.t('navigation.done')}
        </Button>
      </div>}
    >
      <div className="TemplateCategoriesManageModal__header">
        <SearchBox
          placeholder={i18n.t('templatesGrid.manageCategoriesModal.searchPlaceholder')}
          searchExpression={searchExpression}
          onChange={setSearchExpression}
          className="TemplateCategoriesManageModal__header-search-box"
          onSearch={() => {}}
          onClear={() => setSearchExpression('')}
        />
        <Button
          className="TemplateCategoriesManageModal__header-create-new"
          onClick={() => {
            listRef.current.scrollTop = 0;
            setIsNewCategoryRowVisible(!isNewCategoryRowVisible);
          }}
          disabled={isCategoryCreateInProgress}
          data-testid="TemplateCategoriesManageModal__header-create-new"
        >
          {i18n.t('templatesGrid.manageCategoriesModal.create')}
        </Button>
      </div>
      <div
        className={classnames('TemplateCategoriesManageModal__categories', {
          'TemplateCategoriesManageModal__categories--empty': filteredCategories.length === 0
        })}
        ref={listRef}
      >
        <TemplateCategoryNewRowContainer
          isOpen={isNewCategoryRowVisible}
          onSuccess={onCreateCategorySuccess}
          onError={onCreateCategoryError}
          onCancel={hideNewCategoryRow}
          container={listRef.current}
        />
        {shouldShowEmptyState ? (
          <EmptyState
            className="TemplateCategoriesManageModal__categories-empty-state"
            icon={EmptyState.Icons.EMPTY_BOX}
            text={i18n.t('templatesGrid.manageCategoriesModal.emptyState.noCategories')}
          />
        ) : (
          filteredCategories.map(category => (
            <TemplateCategoryRowContainer
              key={category.id}
              category={category}
              container={listRef.current}
              onDeleteClicked={onDeleteClicked}
            />
          ))
        )}
      </div>
      <TemplateCategoryDeleteModalContainer
        isOpen={confirmMethod === CATEGORY_DELETE_CONFIRM_METHOD.SELECT_TARGET}
        category={categoryToDelete}
        onConfirm={onDeleteConfirmed}
        onExited={onDeleteModalExited}
        container={container}
      />
      <ModalConfirmation
        show={confirmMethod === CATEGORY_DELETE_CONFIRM_METHOD.CONFIRM_DELETE}
        title={i18n.t('deleteTemplateCategory.modalTitle')}
        message={<Text hasHTML>
          {i18n.t('deleteTemplateCategory.areYouSure', {title: categoryToDelete && categoryToDelete.title})}
        </Text>}
        onConfirm={onDeleteConfirmed}
        onExited={onDeleteModalExited}
        confirmationButtonLabel={i18n.t('delete')}
        container={container}
      />
    </SimpleModal>
  );
};

TemplateCategoriesManageModal.displayName = 'TemplateCategoriesManageModal';

TemplateCategoriesManageModal.propTypes = {
  container: PropTypes.object,
  categories: PropTypes.arrayOf(templateCategoryType).isRequired,
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  isCategoryCreateInProgress: PropTypes.bool,
  showNotificationAction: PropTypes.func.isRequired,
  fetchCategoryTemplatesAction: PropTypes.func.isRequired,
  deleteTemplateCategoryAction: PropTypes.func.isRequired
};

export default TemplateCategoriesManageModal;
